import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

// ============================================================================
// EVENT UPDATE AND DELETE MUTATIONS
// ============================================================================

/**
 * Update Google Calendar Event
 * Endpoint: PUT /api/unifiedtime/google/create
 */
export const useUpdateGoogleEvent = () => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, eventDetails }: { eventId: string; eventDetails: any }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await fetch("/api/unifiedtime/getCalendar/google", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId, eventDetails }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update event");
      }

      return response.json();
    },
    retry: 3, // Retry up to 3 times on failure
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Google Calendar event updated successfully!",
      });

      queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update Google Calendar event.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Delete Google Calendar Event
 * Endpoint: DELETE /api/unifiedtime/google/create
 */
export const useDeleteGoogleEvent = () => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await fetch("/api/unifiedtime/getCalendar/google", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete event");
      }

      return response.json();
    },
    retry: 3, // Retry up to 3 times on failure
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Google Calendar event deleted successfully!",
      });

      queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete Google Calendar event.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Update Microsoft Calendar Event
 * Endpoint: PUT /api/unifiedtime/getCalendar/msft
 */
export const useUpdateMicrosoftEvent = () => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, eventDetails }: { eventId: string; eventDetails: any }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await fetch("/api/unifiedtime/getCalendar/msft", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId, eventDetails }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update event");
      }

      return response.json();
    },
    retry: 3, // Retry up to 3 times on failure
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Microsoft Calendar event updated successfully!",
      });

      queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update Microsoft Calendar event.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Delete Microsoft Calendar Event
 * Endpoint: DELETE /api/unifiedtime/getCalendar/msft
 */
export const useDeleteMicrosoftEvent = () => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId }: { eventId: string }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await fetch("/api/unifiedtime/getCalendar/msft", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete event");
      }

      return response.json();
    },
    retry: 3, // Retry up to 3 times on failure
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Microsoft Calendar event deleted successfully!",
      });

      queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete Microsoft Calendar event.",
        variant: "destructive",
      });
    },
  });
};
